/**
 * Per-origin marker recording that SOMEBODY who signed in on this browser has at
 * least one passkey enrolled.
 *
 * Be precise about what it is not: it is written from an ACCOUNT-wide fact (the
 * server's list of the signed-in user's credentials, `use-passkeys.ts`), not from
 * anything device-scoped. Nothing correlates the marker to the credential's
 * authenticator, so on a shared machine one person's enrolment can leave the
 * marker set for the next visitor — who then gets the prominent affordance and an
 * empty account chooser, and falls back to the password. That is the accepted
 * cost of the prominence model: annoying, never a leak (the marker names nobody)
 * and never a block (the password path is always rendered).
 *
 * It decides the login affordance's PROMINENCE only (full secondary button vs
 * low-emphasis link) — never whether the feature exists, and never an
 * authorization signal: its presence grants nothing and its absence hides
 * nothing. See `docs/plans/2026-07-25-feat-passkey-webauthn-authentication-plan.md`
 * decision 6.
 *
 * Contents are a bare version marker. No user id, no email address, no
 * credential id: `localStorage` is readable by anything running on the origin
 * and survives sign-out, so an identifier here would be a device-persistent PII
 * leak (AGENTS.md → "Frontend data exposure").
 *
 * Every access is wrapped. Safari private mode throws on `localStorage`, and the
 * getter runs on the login page's render path — an unguarded throw there blanks
 * the sign-in form. The getter degrades to `false`; the setters no-op.
 */

/** Versioned so a future contents change cannot be misread as this one. */
export const PASSKEY_HINT_STORAGE_KEY = 'pp-passkey-hint-v1';

const PASSKEY_HINT_VALUE = '1';

export function hasPasskeyHint(): boolean {
  try {
    return globalThis.localStorage.getItem(PASSKEY_HINT_STORAGE_KEY) === PASSKEY_HINT_VALUE;
  } catch {
    // Storage unavailable (Safari private mode, blocked cookies) — no hint.
    return false;
  }
}

export function setPasskeyHint(): void {
  try {
    globalThis.localStorage.setItem(PASSKEY_HINT_STORAGE_KEY, PASSKEY_HINT_VALUE);
  } catch {
    // Storage unavailable — prominence degrades, the feature still works.
  }
}

export function clearPasskeyHint(): void {
  try {
    globalThis.localStorage.removeItem(PASSKEY_HINT_STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing was written in the first place.
  }
}
